import React, { useState } from "react";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import Reveal from "./Reveal";

// Keep in sync with tf-backend/apps/accounts/src/Modules/subscriptions/plans.ts
const PLANS = [
  {
    name: "Clinic",
    tagline: "For single clinics and solo practices",
    monthly: 1499,
    yearly: 14990,
    limits: ["1 location", "Up to 3 doctors", "300 video minutes / month", "10 GB storage"],
    features: ["OPD appointments & online booking", "Prescriptions & billing", "Video consultations", "Patient app", "ABDM (ABHA) linking"],
  },
  {
    name: "Growth",
    tagline: "For polyclinics and small hospitals",
    monthly: 3999,
    yearly: 39990,
    popular: true,
    limits: ["Up to 3 locations", "Up to 15 doctors", "1,500 video minutes / month", "50 GB storage"],
    features: ["Everything in Clinic", "IPD, wards & bed management", "Pharmacy & diagnostics", "Packages & tariffs", "Roles & permissions"],
  },
  {
    name: "Hospital",
    tagline: "For multi-speciality hospitals",
    monthly: 9999,
    yearly: 99990,
    limits: ["Up to 10 locations", "Up to 60 doctors", "5,000 video minutes / month", "200 GB storage"],
    features: ["Everything in Growth", "Unlimited staff accounts", "Priority support", "Guided onboarding & data import"],
  },
  {
    name: "Enterprise",
    tagline: "For hospital networks",
    monthly: null,
    yearly: null,
    limits: ["Unlimited locations", "Unlimited doctors", "Volume video pricing", "Custom storage"],
    features: ["Everything in Hospital", "Custom integrations", "Dedicated account manager", "SLA-backed uptime"],
  },
];

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const Pricing = () => {
  const [yearly, setYearly] = useState(false);

  return (
    <section id="pricing" className="py-24 sm:py-28">
      <div className="container-x">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="eyebrow">Pricing</span>
          <h2 className="section-title">Simple plans that grow with your practice</h2>
          <p className="mt-4 text-lg text-slate-600">
            Every plan includes the full clinical suite, the patient app and ABDM. No per-patient fees — and your care never stops over a billing question.
          </p>
          <div className="mt-8 inline-flex rounded-xl border border-slate-200 bg-white p-1 text-sm font-semibold shadow-sm">
            {[
              [false, "Monthly"],
              [true, "Yearly"],
            ].map(([value, label]) => (
              <button
                key={label}
                type="button"
                onClick={() => setYearly(value)}
                className={`rounded-lg px-4 py-2 transition ${yearly === value ? "bg-ink-900 text-white" : "text-slate-600 hover:text-ink-900"}`}
              >
                {label}
                {value && <span className={`ml-1.5 text-xs ${yearly ? "text-care-300" : "text-care-600"}`}>2 months free</span>}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {PLANS.map((p, i) => {
            const price = yearly ? p.yearly : p.monthly;
            return (
              <Reveal
                key={p.name}
                delay={i * 0.06}
                className={`relative flex flex-col rounded-3xl border bg-white p-7 ${p.popular ? "border-brand-500 shadow-glow" : "border-slate-200 shadow-soft"}`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold text-white">
                    <Sparkles className="h-3.5 w-3.5" /> Most popular
                  </span>
                )}
                <h3 className="text-xl font-bold">{p.name}</h3>
                <p className="mt-1 text-sm text-slate-500">{p.tagline}</p>
                <p className="mt-6">
                  {price === null ? (
                    <span className="text-3xl font-bold">Let's talk</span>
                  ) : (
                    <>
                      <span className="text-4xl font-bold tracking-tight">{inr(price)}</span>
                      <span className="text-sm text-slate-500"> / {yearly ? "year" : "month"}</span>
                    </>
                  )}
                </p>
                <p className="mt-1 text-xs text-slate-500">{price === null ? "Volume pricing for groups" : yearly ? `≈ ${inr(Math.round(price / 12))} / month · + GST` : "+ 18% GST"}</p>

                <ul className="mt-6 space-y-2 border-b border-slate-100 pb-6 text-sm font-medium text-ink-900">
                  {p.limits.map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
                <ul className="mt-6 flex-1 space-y-3 text-sm text-slate-700">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-care-500" /> {f}
                    </li>
                  ))}
                </ul>
                <a href="#contact" className={`mt-8 ${p.popular ? "btn-primary" : "btn-secondary"}`}>
                  {price === null ? "Contact sales" : "Get started"} <ArrowRight className="h-4 w-4" />
                </a>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mx-auto mt-10 grid max-w-4xl gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-6 text-sm text-slate-600 sm:grid-cols-3">
          <div>
            <p className="font-semibold text-ink-900">Extra video minutes</p>
            <p className="mt-1">₹2 per consultation minute beyond your plan.</p>
          </div>
          <div>
            <p className="font-semibold text-ink-900">Extra storage</p>
            <p className="mt-1">₹10 per GB per month beyond your plan.</p>
          </div>
          <div>
            <p className="font-semibold text-ink-900">Never locked out</p>
            <p className="mt-1">Going over a limit never stops your team. We'll suggest a better plan instead.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;
